/**
 * Per-run boss attempt counter for Explore Unknown tactical retries (sessionStorage).
 * Attempt 1 = first fight (no retry label). Each boss-loss retry bumps before the next battle starts.
 */

export function getExploreUnknownBossAttemptStorageKey(runId, missionIndex) {
  if (runId == null) {
    throw new Error('getExploreUnknownBossAttemptStorageKey: runId is required');
  }
  if (missionIndex == null || !Number.isInteger(Number(missionIndex)) || Number(missionIndex) < 1) {
    throw new Error('getExploreUnknownBossAttemptStorageKey: missionIndex is required');
  }
  return `euBossAttempt:${Number(runId)}:${Number(missionIndex)}`;
}

/**
 * @returns {number} Current attempt for the active boss fight (default 1).
 */
export function readExploreUnknownBossAttemptNumber(runId, missionIndex) {
  const key = getExploreUnknownBossAttemptStorageKey(runId, missionIndex);
  let raw = null;
  try {
    if (typeof sessionStorage !== 'undefined') {
      raw = sessionStorage.getItem(key);
    }
  } catch {
    return 1;
  }
  if (raw == null || raw === '') {
    return 1;
  }
  const n = Number(raw);
  if (!Number.isInteger(n) || n < 1) {
    throw new Error(
      `readExploreUnknownBossAttemptNumber: invalid stored attempt "${raw}" for ${key}`
    );
  }
  return n;
}

/**
 * Bump attempt before starting a boss-loss retry battle.
 * @returns {number} Attempt number for the upcoming fight (2 = "2nd try", etc.).
 */
export function bumpExploreUnknownBossAttemptForRetry(runId, missionIndex) {
  const key = getExploreUnknownBossAttemptStorageKey(runId, missionIndex);
  const next = readExploreUnknownBossAttemptNumber(runId, missionIndex) + 1;
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.setItem(key, String(next));
    }
  } catch (err) {
    throw new Error(`bumpExploreUnknownBossAttemptForRetry: failed to persist attempt for ${key}`);
  }
  return next;
}

export function clearExploreUnknownBossAttemptNumber(runId, missionIndex) {
  const key = getExploreUnknownBossAttemptStorageKey(runId, missionIndex);
  try {
    if (typeof sessionStorage !== 'undefined') {
      sessionStorage.removeItem(key);
    }
  } catch {
    /* ignore blocked storage */
  }
}

/**
 * @param {number} attemptNumber
 * @returns {string|null} i18n key under exploreUnknownPage for retry label (null when attempt < 2)
 */
export function getExploreUnknownBossRetryAttemptTranslationKey(attemptNumber) {
  const n = Number(attemptNumber);
  if (!Number.isInteger(n) || n < 2) {
    return null;
  }
  if (n === 2) return 'exploreUnknownPage.bossIntroRetryAttempt2';
  if (n === 3) return 'exploreUnknownPage.bossIntroRetryAttempt3';
  if (n === 4) return 'exploreUnknownPage.bossIntroRetryAttempt4';
  return 'exploreUnknownPage.bossIntroRetryAttemptNth';
}
